import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { SONG_JOURNEY_STAGES } from "@/types/SongOrder";
import { buildMusicPlayerUrl } from "@/utils/helper";
import { CheckIcon } from "./SongIcons";

interface SongJourneyTimelineProps {
  /** Number of leading stages rendered as complete. */
  completedCount: number;
  /** Recipient name interpolated into the stage descriptions. */
  recipientName: string;
  docId?: string | null;
  userId?: string | null;
}

function PlayerAccessLink({
  docId,
  userId,
  children,
}: {
  docId: string;
  userId: string;
  children?: React.ReactNode;
}) {
  const { locale } = useParams<{ locale?: string }>();
  const playerUrl = buildMusicPlayerUrl(docId, userId, locale ?? "", true);

  return (
    <a
      href={playerUrl}
      id="accessSongNow"
      className="font-normal text-[#0e76ff] underline hover:text-[#005fcc]"
      onClick={(event) => {
        event.preventDefault();
        window.location.assign(playerUrl);
      }}
    >
      {children}
    </a>
  );
}

export default function SongJourneyTimeline({
  completedCount,
  recipientName,
  docId,
  userId,
}: SongJourneyTimelineProps) {
  const { t } = useTranslation();
  const canOpenPlayer = Boolean(docId && userId);

  return (
    <ol className="flex w-full flex-col">
      {SONG_JOURNEY_STAGES.map((stage, index) => {
        const isCompleted = index < completedCount;
        const isLast = index === SONG_JOURNEY_STAGES.length - 1;
        const isConnectorActive = index + 1 < completedCount;

        return (
          <li key={stage} className="flex gap-4">
            <div className="flex flex-col items-center">
              <span
                className={[
                  "flex size-6 sm:size-10 shrink-0 items-center justify-center rounded-full transition-colors duration-500",
                  isCompleted
                    ? "bg-[#0e76ff] text-white"
                    : "bg-[#d7e6f7] text-white/70 dark:bg-[#264a73]",
                ].join(" ")}
              >
                <CheckIcon size={16} className="size-3 sm:size-4.5" />
              </span>
              {!isLast && (
                <span
                  className={[
                    "w-[2px] flex-1 transition-colors duration-500",
                    isConnectorActive
                      ? "bg-[#0e76ff]"
                      : "bg-[#d7e6f7] dark:bg-[#264a73]",
                  ].join(" ")}
                />
              )}
            </div>

            <div className={`flex flex-col gap-1 ${isLast ? "" : "pb-4"}`}>
              <h3
                className={[
                  "text-[14px] sm:text-[23px] font-semibold leading-[1.3] transition-colors duration-500",
                  isCompleted
                    ? "text-[#16283d] dark:text-[#fdfdfd]"
                    : "text-[#8fa3ba] dark:text-[#8ba3bf]",
                ].join(" ")}
              >
                {t(`songOrder.journey.stages.${stage}.title`)}
              </h3>
              {stage === "delivered" ? (
                <p
                  className={[
                    "max-w-[360px] sm:max-w-[540px] text-[13px] sm:text-[15px] font-normal leading-[1.45] transition-colors duration-500",
                    isCompleted
                      ? "text-[#687585] dark:text-[#a1a7b0]"
                      : "text-[#a3b4c6] dark:text-[#7c93ae]",
                  ].join(" ")}
                >
                  {isCompleted && canOpenPlayer ? (
                    <Trans
                      i18nKey="songOrder.journey.stages.delivered.accessNote"
                      components={{
                        accessLink: (
                          <PlayerAccessLink docId={docId!} userId={userId!} />
                        ),
                      }}
                    />
                  ) : isCompleted ? (
                    t("songOrder.journey.stages.delivered.accessNote")
                  ) : (
                    t("songOrder.journey.stages.delivered.ongoingaccessNote")
                  )}
                </p>
              ) : (
                <p
                  className={[
                    "max-w-[360px] sm:max-w-[540px] text-[13px] sm:text-[16px] font-normal leading-[1.45] transition-colors duration-500",
                    isCompleted
                      ? "text-[#687585] dark:text-[#a1a7b0]"
                      : "text-[#a3b4c6] dark:text-[#7c93ae]",
                  ].join(" ")}
                >
                  {t(`songOrder.journey.stages.${stage}.description`, {
                    name: recipientName,
                  })}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
